import React from 'react';
import { Box, Flex, IconButton, Text, Tooltip } from 'pounceV2';
import { useTheme } from '@mui/material';
import { datadogRum } from '@datadog/browser-rum';
import { useAppConfig } from 'Components/utils/AppBootstrapper/AppConfigContext';
import useModalV3 from 'Hooks/useModalV3';
import AIFeedbackModalV3 from './AIFeedbackModalV3';
import type { FeatureContext } from './index';

const FEEDBACK_FADE_OUT_DELAY_MS = 3000;

export interface AIResponseFeedbackProps {
  featureContext: FeatureContext;
  promptText?: string;
  onFeedbackSubmitted?: (sentiment: 'positive' | 'negative') => void;
}

interface FeedbackTrackingData {
  sentiment: 'positive' | 'negative';
  reasons?: string[];
  // eslint-disable-next-line camelcase
  additional_feedback?: string;
  // eslint-disable-next-line camelcase
  user_prompt?: string;
}

const AIResponseFeedbackV3: React.FC<AIResponseFeedbackProps> = ({
  featureContext,
  promptText,
  onFeedbackSubmitted,
}) => {
  const theme = useTheme();
  const { tokens } = theme.palette;
  const { showModal } = useModalV3();
  const [submitted, setSubmitted] = React.useState(false);
  const [shouldFadeOut, setShouldFadeOut] = React.useState(false);
  const { accountId, customerName, datadogRumEnabled } = useAppConfig();

  // Fade out after submission
  React.useEffect(() => {
    if (!submitted) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setShouldFadeOut(true);
    }, FEEDBACK_FADE_OUT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [submitted]);

  /**
   * Tracks AI response feedback to Datadog RUM.
   *
   * Event name: 'ai_response_feedback'
   *
   * Schema:
   * - sentiment: 'positive' | 'negative'
   * - feature_context: 'alert_overview' | 'alert_triage'
   * - reasons?: string[] (negative only - array of reasons)
   * - additional_feedback?: string (optional)
   * - user_prompt?: string (only if user opts in)
   * - url: string
   * - customer_id: string
   * - customer_name?: string
   * - timestamp: number
   */
  const trackFeedback = React.useCallback(
    (data: FeedbackTrackingData) => {
      if (datadogRumEnabled) {
        datadogRum.addAction('ai_response_feedback', {
          ...data,
          feature_context: featureContext,
          url: window.location.href,
          customer_id: accountId,
          customer_name: customerName,
          timestamp: Date.now(),
        });
      }
    },
    [featureContext, accountId, customerName, datadogRumEnabled],
  );

  const handlePositive = React.useCallback(() => {
    trackFeedback({ sentiment: 'positive' });
    setSubmitted(true);
    onFeedbackSubmitted?.('positive');
  }, [trackFeedback, onFeedbackSubmitted]);

  const handleModalSubmit = React.useCallback(
    (reasons: string[], additionalFeedback?: string, editedPrompt?: string) => {
      trackFeedback({
        sentiment: 'negative',
        reasons,
        additional_feedback: additionalFeedback?.trim() || undefined,
        user_prompt: editedPrompt,
      });
      setSubmitted(true);
      onFeedbackSubmitted?.('negative');
    },
    [trackFeedback, onFeedbackSubmitted],
  );

  const handleNegative = React.useCallback(() => {
    showModal(<AIFeedbackModalV3 onSubmit={handleModalSubmit} promptText={promptText} />, {
      title: 'Help us improve',
    });
  }, [showModal, handleModalSubmit, promptText]);

  return (
    <>
      <Box
        opacity={shouldFadeOut ? 0 : 1}
        maxHeight={shouldFadeOut ? 0 : '100px'}
        overflow="hidden"
        transition="opacity 0.5s ease-out, max-height 0.5s ease-out"
      >
        <Flex align="center" gap={2}>
          {!submitted && (
            <>
              <Text fontWeight="medium" color={tokens?.content?.text?.neutral?.[3] || 'gray-300'}>
                Was this helpful?
              </Text>
              <Tooltip content="Yes, this was helpful" placement="top">
                <IconButton
                  icon="thumbs-up"
                  aria-label="Positive feedback"
                  variant="ghost"
                  size="small"
                  onClick={handlePositive}
                  data-tid="ai-feedback-positive"
                />
              </Tooltip>
              <Tooltip content="No, this was not helpful" placement="top">
                <IconButton
                  icon="thumbs-down"
                  aria-label="Negative feedback"
                  variant="ghost"
                  size="small"
                  onClick={handleNegative}
                  data-tid="ai-feedback-negative"
                />
              </Tooltip>
            </>
          )}

          {submitted && (
            <Text
              fontWeight="medium"
              color={tokens?.content?.text?.semantic?.info?.base || 'blue-300'}
              aria-live="polite"
            >
              Thank you for your feedback!
            </Text>
          )}
        </Flex>
      </Box>
    </>
  );
};

export default React.memo(AIResponseFeedbackV3);
